import { useEffect, useState } from "react"
import { DateTime } from "luxon"
import { makeStyles } from "tss-react/mui"
import { Grid } from "@mui/material"
import { DatesSetArg } from "@fullcalendar/core"
import { StandardCalendar } from "../../../../../../shared/components/calendar/StandardCalendar"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { ProductionSlipDialog } from "../components/ProductionSlipDialog"
import { ProductionSlip } from "../ordersForCalendarTypes"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { ExpandableSearch } from "../components/ExpandableSearch"
import {
    MerchantOrdersForCalendarQuery,
    OrdersForCalendarResponse,
    fetchOrdersForCalendar,
} from "../ordersForCalendarApi"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../localization/useDateTime"
import { OrderPackageSlip } from "../../meetingService/components/OrderPackageSlip"
import { CalendarEvent } from "../../../../../../shared/components/calendar/calendarTypes"

const useStyles = makeStyles()((theme) => ({
    container: {
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: "0.75rem",
        gap: "1.25rem",
        padding: "1rem",
        marginTop: "1.5rem",
    },
}))

const selectOrders = (data: OrdersForCalendarResponse): CalendarEvent[] => {
    return (
        data?.orders.map((order) => ({
            id: order.id,
            uid: order.uid,
            title: order.calendarDetails.title,
            start: order.calendarDetails.start,
            deliveryDetails: order.deliveryDetails,
            end: order.calendarDetails.end,
            meetingDetails: order.meetingDetails,
        })) || []
    )
}

export default function OrdersForCalendarTab() {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const dateTimeFactory = useDateTime()

    const [orderModalState, setOrderModalState] = useState<{
        show: boolean
        list: null | ProductionSlip[]
    }>({
        show: false,
        list: null,
    })

    const [filteredEvents, setFilteredEvents] = useState<CalendarEvent[]>([])
    const [showPackSlipModal, setShowPackSlipModal] = useState<{ open: boolean; id?: number }>({ open: false })
    const [startDate, setStartDate] = useState<DateTime>(DateTime.now().startOf("month"))
    const [endDate, setEndDate] = useState<DateTime>(DateTime.now().endOf("month"))
    const [searchText, setSearchText] = useState("")
    const { l10n } = useAppLocalization()

    const { data: events = [] } = useApiQuery(fetchOrdersForCalendar, {
        queryName: MerchantOrdersForCalendarQuery.LIST,
        dependencies: {
            merchantId,
            startDate: startDate.toISODate(),
            endDate: endDate.toISODate(),
        },
        select: selectOrders,
    })

    const filterEvents = (text: string, currentEvents: CalendarEvent[]) => {
        if (text) {
            const lowercasedText = text.toLowerCase()
            const filtered = currentEvents.filter(
                (event) =>
                    String(event.id).toLowerCase().includes(lowercasedText) ||
                    event.title.toLowerCase().includes(lowercasedText),
            )
            setFilteredEvents(filtered)
        } else {
            setFilteredEvents(currentEvents)
        }
    }

    useEffect(() => {
        filterEvents(searchText, events)
    }, [searchText, events])

    const handleDatesSet = (datesSetInfo: DatesSetArg) => {
        setStartDate(dateTimeFactory.fromJSDate(datesSetInfo.start))
        setEndDate(dateTimeFactory.fromJSDate(datesSetInfo.end))
    }

    const additionalCalendarMenuItems = [
        {
            key: "production-slip",
            label: l10n.getString("meeting-service-table-production-slip-button"),
            onClick: (id: number) => setShowPackSlipModal({ open: true, id }),
        },
    ]

    return (
        <Grid container flexDirection="column" className={classes.container}>
            <Grid container justifyContent="flex-end" alignItems="center" flexWrap="nowrap">
                <LocalizedStrict id="orders-for-calendar-search" attrs={{ label: true, placeholder: true }}>
                    <ExpandableSearch
                        value={searchText}
                        onChange={setSearchText}
                        label="Search"
                        placeholder="Search for order id or order name"
                    />
                </LocalizedStrict>
            </Grid>
            <StandardCalendar
                additionalMenuItems={additionalCalendarMenuItems}
                events={filteredEvents}
                handleDatesSet={handleDatesSet}
            />
            {showPackSlipModal.open && showPackSlipModal.id && (
                <OrderPackageSlip
                    orders={events}
                    onClose={() => setShowPackSlipModal({ open: false })}
                    merchantId={merchantId}
                    id={showPackSlipModal.id}
                    orderIds={[]}
                    open={showPackSlipModal.open}
                />
            )}

            {orderModalState.show && orderModalState.list && (
                <ProductionSlipDialog
                    list={orderModalState.list}
                    open={orderModalState.show}
                    onClose={() =>
                        setOrderModalState({
                            show: false,
                            list: null,
                        })
                    }
                />
            )}
        </Grid>
    )
}
